import { useState, useEffect} from 'react';
import { TableRow, TableCell, Tooltip, Paper, Stack, Button, Grid, Typography, MenuItem, Box, FormControl, InputLabel, Select, Checkbox, ListItemText, OutlinedInput, setRef } from '@mui/material'
import DBQuoteOfTheDay from './DashboardComponents/DBQuoteOfTheDay'
import DBPortfolioHighLevel from './DashboardComponents/DBPortfolioHighLevel'
import DBPortfolioDetailed from './DashboardComponents/DBPortfolioDetailed'
import DBSectorPerformance from './DashboardComponents/DBSectorPerformance';
import DBGainersAndLosers from './DashboardComponents/DBGainersAndLosers';
import TickerSearch from './TickerSearch.js'
import axios from 'axios';

import {useAtomValue} from 'jotai';
import { emailAtom } from './Atoms.js';

import { BarChart, Bar, Cell, YAxis } from "recharts";
import { marketNamesWithPINX as marketNames, regionNames, sectorNames, marketCapValues } from "./Constants/constants";
import { useNavigate } from 'react-router-dom';

import { performanceColorCellFromValue } from "./utils/PerformanceColourScale.js"

const WEBAPI = process.env.REACT_APP_WEB_API;

const TIMEFRAMES = {
  '1M': 'pricechg1months',
  '3M': 'pricechg3months',
  '6M': 'pricechg6months',
  '1Y': 'pricechg1years',
  '3Y': 'pricechg3years',
}


function Dashboard() {
  const [market, setMarket] = useState("CAN");
  const [regions, setRegions] = useState([]);
  const [marketCapMin, setMarketCapMin] = useState(0);
  const [marketCapMax, setMarketCapMax] = useState(10000000);
  const [regionDisplay, setRegionDisplay] = useState("none");
  const [refreshEnabled, setRefreshEnabled] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [companies, setCompaines] = useState([])
  const [timeframe, setTimeframe] = useState('1M')
  const [searchEnabled, setSearchEnabled] = useState(true)
  const navigate = useNavigate()
 
  const userEmail = useAtomValue(emailAtom);
  
  const adminEmails = ["mkacur@fsavaluation.com", "ken@afxsoftware.com", "kenc@team-eagle.ca"];
  const isAdmin = adminEmails.includes(userEmail);

  useEffect(() => {
    const fetchResults = async () => {
      setSearchEnabled(false)
      setRefreshEnabled(false)

      const searchResults = await getSearchResults()
      setCompaniesData(searchResults)

      //const trimmedResults = searchResults

      setCompaines(searchResults)
    };

    if(searchEnabled) fetchResults()
  }, [searchEnabled])


const getSearchResults = async () => {
  // extract sector codes
  const sectorCodes = sectorNames.map(s => s.value);
  // fetch each sector in parallel
  const allResults = await Promise.all(
    sectorCodes.map(async (code) => {
      const payload = {
        market,
        regions,
        sector: code,
        marketCapMin,
        marketCapMax,
      };

      try {
        const res = await axios.post(`${WEBAPI}/quicksector/results`, payload);

        // process charts exactly like your existing logic
        res.data.forEach((row) => {
            const charts = ["chart_cfirrEx", "chart_basicEBITDA", "chart_dividendYield", "chart_revenue"];
  
            charts.forEach(chartName => {
              if (!row[chartName]) {
                row[chartName] = [];
                return;
              }

              row[chartName] = row[chartName].split(",").map(Number).map((item, idx) => {
                let color;
                if (chartName === "chart_cfirrEx") color = item > 0 ? "green" : "red";
                else if (chartName === "chart_basicEBITDA" || chartName === "chart_dividendYield") color = item > 0 ? "cornflowerblue" : "red";
                else if (chartName === "chart_revenue") color = idx <= 5 ? (item > 0 ? "cornflowerblue" : "red") : (item > 0 ? "lightblue" : "lightred");
                return { y: item, color };
              });
            });
          
          // attach sector for grouping later
          row.sector = code;
        });

        return res.data;
      } catch (err) {
        console.error("Error fetching sector", code, err);
        return [];
      }
    })
  );

  // flatten array of arrays into a single array
  console.log(allResults)
  const combinedResults = allResults.flat();
  console.log(TIMEFRAMES[timeframe])
  combinedResults.sort((a, b) => b[TIMEFRAMES[timeframe]] - a[TIMEFRAMES[timeframe]])
  console.log("Combined results:", combinedResults);
  return combinedResults;
};

  const handleMarketChange = (event) => {
    const newMarket = event.target.value;
    //console.log(newMarket);
    setMarket(newMarket);

    if (newMarket === "PNX") {
      setRegionDisplay("block");
    } else {
      setRegionDisplay("none");
    }
    setRegions([]);
    localStorage.setItem("QS_Regions", []);

    localStorage.setItem("QS_Market", newMarket);

    setRefreshEnabled(true);
  };

  const handleRegionChange = (event) => {
    const newRegions = event.target.value;
    //console.log(newRegions);

    setRegions(newRegions);
    localStorage.setItem("QS_Regions", newRegions);

    setRefreshEnabled(true);
  };

  const handleMarketCapMinChange = (event) => {
    const newMarketCapMin = event.target.value;
    //console.log(newMarketCapMin);

    setMarketCapMin(newMarketCapMin);
    localStorage.setItem("QS_MarketCapMin", newMarketCapMin);

    setRefreshEnabled(true);
  };

  const handleMarketCapMaxChange = (event) => {
    const newMarketCapMax = event.target.value;
    //console.log(newMarketCapMax);

    setMarketCapMax(newMarketCapMax);
    localStorage.setItem("QS_MarketCapMax", newMarketCapMax);

    setRefreshEnabled(true);
  };

  const getMCapMinValues = () => {
    const mCapMinValues = marketCapValues.filter((mCap) => mCap.mCapMin === true && mCap.value < marketCapMax);
    return mCapMinValues;
  };

  const getMCapMaxValues = () => {
    const mCapMaxValues = marketCapValues.filter((mCap) => mCap.mCapMax === true && mCap.value > marketCapMin);
    return mCapMaxValues;
  };

  const handleTimeframeChange = (event) => {
    const newTimeframe = event.target.value
    setTimeframe(newTimeframe)
    setRefreshEnabled(true);

    // const newOrder = companiesData

    // newOrder.sort((a, b) => b[TIMEFRAMES[newTimeframe]] - a[TIMEFRAMES[newTimeframe]])

    // setCompaines(newOrder)
  }

  const handleTicker = (newTicker) => {
    if (!newTicker) return;
    console.log(newTicker)
    navigate(`/${newTicker.toLowerCase()}`); 
  };

  console.log(companies)
  return (
    <Grid container sx={{ p: { xs: 2, sm: 4 } }}>
      <Grid item xs={12}>        
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  width: "100%",
                  "& .MuiAutocomplete-root": { width: "100% !important" },
                  "& .MuiInputBase-root": {
                    width: "100% !important",
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  "& .MuiAutocomplete-option": { fontSize: "0.75rem" },
                  "& .MuiAutocomplete-popupIndicator": { display: "none" },
                }}
              >
                <TickerSearch
                  handleAddTicker={handleTicker}
                  fromCompanyDetail={true}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={10}>
              <DBQuoteOfTheDay />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
      <Paper sx={{ my: 1, p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 0, width: 150 }} size="small">
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                onChange={handleTimeframeChange}
                input={<OutlinedInput label="Timeframe" />}
              >
                {Object.keys(TIMEFRAMES).map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 0, width: 110 }} size="small">
              <InputLabel>Market</InputLabel>
              <Select
                value={market}
                onChange={handleMarketChange}
                input={<OutlinedInput label="Market" />}
              >
                {marketNames.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm="auto" sx={{ display: regionDisplay }}>
            <FormControl sx={{ m: 0, minWidth: 200 }} size="small">
              <InputLabel>Regions</InputLabel>
              <Select
                multiple
                value={regions}
                onChange={handleRegionChange}
                input={<OutlinedInput label="Regions" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {regionNames.map((region) => (
                  <MenuItem key={region.value} value={region.value}>
                    <Checkbox checked={regions.indexOf(region.value) > -1} />
                    <ListItemText primary={`${region.label} - ${region.value}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm="auto">
            <FormControl sx={{ m: 0, width: 150 }} size="small">
              <InputLabel>Market Cap Min</InputLabel>
              <Select
                value={marketCapMin}
                onChange={handleMarketCapMinChange}
                input={<OutlinedInput label="Market Cap Min" />}
              >
                {getMCapMinValues().map((cap) => (
                  <MenuItem key={cap.value} value={cap.value}>{cap.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm="auto">
            <FormControl sx={{ m: 0, width: 150 }} size="small">
              <InputLabel>Market Cap Max</InputLabel>
              <Select
                value={marketCapMax}
                onChange={handleMarketCapMaxChange}
                input={<OutlinedInput label="Market Cap Max" />}
              >
                {getMCapMaxValues().map((cap) => (
                  <MenuItem key={cap.value} value={cap.value}>{cap.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={() => setSearchEnabled(true)}
              disabled={!refreshEnabled}
            >
              Refresh
            </Button>
          </Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1, // take remaining space
              maxWidth: 250,
              mt: { xs: 2, sm: 0 },
              ml: "auto"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid currentColor",
                mb: 1,
              }}
            >
              <Typography variant="body2">Stock Market %</Typography>
              <Typography variant="body2" sx={{ pr: 1 }}>24 Qtrs</Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              {["3Y", "1Y", "6M", "3M", "1M"].map((label) => (
                <Box key={label} sx={{ flex: 0.25, textAlign: "left" }}>
                  <Typography fontSize="small" fontWeight="bold">{label}</Typography>
                </Box>
              ))}
              <Box sx={{ flex: 1, textAlign: "right" }}>
                  <Typography fontSize="small" fontWeight="bold" color="green">CFIRR</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        </Paper>
      </Grid>

      {isAdmin && (
      <Grid container spacing={2}>
        {sectorNames.map((sector) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={sector.value}>        
            <Paper sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography key={sector.value} value={sector.value} varient='h6' sx={{ fontWeight: 'bold', marginBottom: 1, color: "#4e95d9", }}>
              {sector.label.split(" - ")[0]}
                {companies.filter(row => row.sector === sector.value).slice(0, 4).map((row) => (
                  <TableRow sx={{ cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.15)" }, paddingTop: 1, color: "black" }} onClick={() => navigate(`/${row.ticker.toLowerCase()}`)}>
                    <TableRow>
                      <TableCell padding="none" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", width: "100px", minWidth: "80px", }}>
                        {row.ticker}
                      </TableCell>
                      <TableCell padding="none" sx={{ 
                        fontFamily: "ArchivoNarrow-Regular", 
                        fontSize: "0.75rem", 
                        width: "50%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}>
                        {row.company}
                      </TableCell>
                      <TableCell padding="none" sx={{ fontSize: "0.5rem", paddingLeft: 1, width: "10%", textAlign: "center" }} align="center">
                        <Tooltip title={row.domicile_country_flag}>
                          <img src={`https://flagcdn.com/w20/${row.domicile_country_flag?.toLowerCase()}.png`} width="20" alt="flag" />
                        </Tooltip>
                      </TableCell>
                      <TableCell padding="none" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", paddingLeft: 1, width: "60px", textAlign: "right" }}>
                        {row.sector}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} padding="none">
                        <Box display="flex" alignItems="center" width="100%">
                          <Box sx={{ 
                            fontFamily: "ArchivoNarrow-Regular", 
                            fontSize: "10px", 
                            backgroundColor: performanceColorCellFromValue(row.pricechg3years),
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.25,
                            py: 1
                            }}>
                            {typeof row.pricechg3years === "number"
                              ? `${row.pricechg3years}%`
                              : row.pricechg3years}
                          </Box>
                          <Box sx={{ 
                            fontFamily: "ArchivoNarrow-Regular", 
                            fontSize: "10px", 
                            backgroundColor: performanceColorCellFromValue(row.pricechg1years),
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.25,
                            py: 1
                            }}>
                            {typeof row.pricechg1years === "number"
                              ? `${row.pricechg1years}%`
                              : row.pricechg1years}
                          </Box>
                          <Box sx={{ 
                            fontFamily: "ArchivoNarrow-Regular", 
                            fontSize: "10px", 
                            backgroundColor: performanceColorCellFromValue(row.pricechg6months),
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.25,
                            py: 1 
                            }}>
                            {typeof row.pricechg6months === "number"
                              ? `${row.pricechg6months}%`
                              : row.pricechg6months}
                          </Box>
                          <Box sx={{ 
                            fontFamily: "ArchivoNarrow-Regular", 
                            fontSize: "10px", 
                            paddingLeft: 0.5, 
                            backgroundColor: performanceColorCellFromValue(row.pricechg3months),
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.25,
                            py: 1 
                            }}>
                            {typeof row.pricechg3months === "number"
                              ? `${row.pricechg3months}%`
                              : row.pricechg3months}
                          </Box>
                          <Box sx={{ 
                            fontFamily: "ArchivoNarrow-Regular", 
                            fontSize: "10px", 
                            paddingLeft: 0.5, 
                            backgroundColor: performanceColorCellFromValue(row.pricechg1months),
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.25,
                            py: 1
                            }}>
                            {typeof row.pricechg1months === "number"
                              ? `${row.pricechg1months}%`
                              : row.pricechg1months}
                          </Box>
                          <Box sx={{ width: 100, minWidth: 100, maxWidth: 100, paddingLeft: 1 }}>
                            {row.sector === "FI" ? (
                              <Typography align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem" }}>
                                N/A
                              </Typography>
                            ) : (
                              <Box display="flex" justifyContent="center" alignItems="center">
                                <BarChart 
                                  width={100} 
                                  height={30} 
                                  data={row.chart_cfirrEx} 
                                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                                >
                                  <YAxis type="number" domain={[-10, 30]} hide={true} allowDataOverflow={true} />
                                  <Bar dataKey="y">
                                    {row.chart_cfirrEx.map((entry, index) => (
                                      <Cell 
                                        key={index} 
                                        fill={entry.color}
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableRow>
                ))}
            </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      )}


      {/* <Grid item xs={12} md={6}>
        <DBPortfolioHighLevel />
      </Grid>
      <Grid item xs={12} md={6}>
        <DBPortfolioDetailed />
      </Grid>
      <Grid item xs={12} md={6}>
        <DBSectorPerformance />
      </Grid>
      <Grid item xs={12} md={6}>
        <DBGainersAndLosers />
      </Grid> */}
    </Grid>);
}

export default Dashboard;
